import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileText, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { FormTemplate } from '@/types/documentRequestTypes';
import TemplateDesigner from './TemplateDesigner';

interface TemplateDesignerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: FormTemplate;
  onSuccess: () => void;
}

const TemplateDesignerModal: React.FC<TemplateDesignerModalProps> = ({
  open,
  onOpenChange,
  template,
  onSuccess
}) => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  const [step, setStep] = useState<'upload' | 'design'>(template ? 'design' : 'upload');
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [templateData, setTemplateData] = useState<FormTemplate | undefined>(template);

  // Handle file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF file');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadedFile(file);
  };

  // Upload PDF and proceed to designer
  const handleProceedToDesigner = async () => {
    if (!uploadedFile || !organizationId) return;

    setUploading(true);
    try {
      // Upload the PDF file to storage
      const fileName = `${Date.now()}_${uploadedFile.name}`;
      const filePath = `${organizationId}/templates/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('form-templates')
        .upload(filePath, uploadedFile, {
          contentType: 'application/pdf',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Failed to upload file: ${uploadError.message}`);
      }

      // Create a temporary template object for the designer
      const newTemplate: FormTemplate = {
        // Don't include id for new templates - let database generate it
        organization_id: organizationId,
        name: uploadedFile.name.replace(/\.[^/.]+$/, ""), // Remove extension
        description: '',
        category: 'other',
        template_file_url: filePath, // Store the file path, not the signed URL
        is_active: true,
        created_by: user?.id || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        signature_fields: [],
        form_fields: [],
        template_settings: {
          signing_order: 'parallel',
          expiration_days: 30,
          reminder_schedule: [1, 3, 7],
          require_authentication: false,
          allow_decline: true,
          brand_color: '#3B82F6'
        }
      };

      setTemplateData(newTemplate);
      setStep('design');
      toast.success('PDF uploaded successfully. Now add your signature fields.');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      toast.error('Failed to upload PDF file');
    } finally {
      setUploading(false);
    }
  };

  // Save template
  const handleSaveTemplate = async (updates: Partial<FormTemplate>) => {
    if (!templateData || !organizationId) return;

    try {
      if (template?.id) {
        // Update existing template
        const templateToSave = {
          ...templateData,
          ...updates,
          organization_id: organizationId,
          created_by: user?.id || '',
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('form_templates')
          .update(templateToSave)
          .eq('id', template.id);

        if (error) throw error;
      } else {
        // Create new template - exclude id field to let database generate it
        const { id, ...templateToSave } = {
          ...templateData,
          ...updates,
          organization_id: organizationId,
          created_by: user?.id || '',
          updated_at: new Date().toISOString()
        };

        const { error } = await supabase
          .from('form_templates')
          .insert([templateToSave]);

        if (error) throw error;
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving template:', error);
      throw error;
    }
  };

  // Reset state when modal closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setStep(template ? 'design' : 'upload');
      setUploadedFile(null);
      setTemplateData(template);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            {template ? 'Edit Template' : 'Create New Template'}
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <div className="space-y-6 p-6">
            <Alert>
              <FileText className="h-4 w-4" />
              <AlertDescription>
                Upload a PDF document that you want to convert into an interactive form. 
                You'll be able to add signature fields, text inputs, and other form elements in the next step.
              </AlertDescription>
            </Alert>

            {/* File Upload */}
            <div className="space-y-4">
              <Label htmlFor="pdf-upload">Upload PDF Document</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                {!uploadedFile ? (
                  <div>
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-lg font-medium text-gray-900 mb-2">
                      Upload your PDF form
                    </p>
                    <p className="text-gray-600 mb-4">
                      Click to browse or drag and drop your PDF file here
                    </p>
                    <input
                      id="pdf-upload"
                      type="file"
                      accept=".pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => document.getElementById('pdf-upload')?.click()}
                    >
                      Choose PDF File
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-4">
                    <FileText className="h-12 w-12 text-green-600" />
                    <div className="text-left">
                      <p className="font-medium text-green-700">{uploadedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setUploadedFile(null)}
                    >
                      Remove
                    </Button>
                  </div>
                )}
              </div>

              {uploadedFile && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-blue-900 mb-1">Ready to Design</p>
                      <p className="text-blue-700">
                        Your PDF is ready. Click "Proceed to Designer" to start adding signature fields and form elements.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleProceedToDesigner}
                disabled={!uploadedFile || uploading}
              >
                {uploading ? 'Uploading...' : 'Proceed to Designer'}
              </Button>
            </div>
          </div>
        )}

        {step === 'design' && templateData && (
          <div className="h-[85vh] overflow-auto">
            <TemplateDesigner
              template={templateData}
              onSave={handleSaveTemplate}
              onCancel={() => onOpenChange(false)}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TemplateDesignerModal;